const BASE_URL = 'https://aisummercamp.worldotutor.com/api/admin/payments';

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-type': 'application/json' },
    ...options
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }

  return res.json();
}

export interface IAdminPayment {
  _id: string;
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  razorpay_signature?: string;
  studentData?: {
    name: string;
    email: string;
    phone: string;
    age?: number;
    experience?: string;
  };
  workshop?: {
    title: string;
    offerPrice?: number;
    price?: string;
    priceCurrency?: string;
    category?: string;
    hours?: string;
    sub?: string;
  };
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  orderNotes?: any;
  emailSent?: boolean;
  adminNotified?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  success: boolean;
  data: IAdminPayment[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

const get = async (
  currentPage: number,
  resultsPerPage: number,
  filters: Record<string, string>,
  sort: string,
  dir: string
): Promise<{ count: number; data: IAdminPayment[] }> => {
  const params = new URLSearchParams({
    limit: resultsPerPage.toString(),
    page: currentPage.toString(),
    sort,
    dir,
    ...filters
  });

  const body = await request<ListResponse>(`${BASE_URL}?${params}`);

  return {
    count: body.pagination?.total || 0,
    data: body.data || []
  };
};

const getById = async (id: string): Promise<IAdminPayment> => {
  const body = await request<{ success: boolean; data: IAdminPayment }>(`${BASE_URL}/${id}`);
  return body.data;
};

const create = async (payload: any): Promise<IAdminPayment> => {
  const body = await request<{ success: boolean; data: IAdminPayment }>(`${BASE_URL}`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  return body.data;
};

const update = async (id: string, payload: Partial<IAdminPayment>): Promise<IAdminPayment> => {
  const body = await request<{ success: boolean; data: IAdminPayment }>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
  return body.data;
};

const remove = async (id: string): Promise<void> => {
  await request(`${BASE_URL}/${id}`, { method: 'DELETE' });
};

const bulkRemove = async (ids?: string[]): Promise<void> => {
  const params = ids ? `?ids=${ids.join(',')}` : '?all=true';
  await request(`${BASE_URL}${params}`, { method: 'DELETE' });
};

const adminPaymentsApi = {
  get,
  getById,
  create,
  update,
  remove,
  bulkRemove
};

export default adminPaymentsApi;
